// The Vitals stage's configurable Symptoms card (Admin → Clinical → Form
// Customization; backend routes/symptom-form.js, utils/symptomForm.js).
//
// A visit records two things in its Vitals stage_data:
//   symptom_form — { answers: [{ key, label, type, value }] }, a snapshot of
//                  what was asked and answered, so renaming or removing a field
//                  later never changes an earlier visit;
//   symptoms     — the same answers as one line of text (composeSymptomsText),
//                  which the queue, Patient Records, the AI context and the
//                  vitals.symptoms column already read.

export type SymptomFieldType =
  | 'text' | 'textarea' | 'number' | 'date'
  | 'dropdown' | 'radio' | 'multiselect' | 'checkboxes' | 'checkbox';

export interface SymptomField {
  id: number;
  field_key: string;
  label: string;
  field_type: SymptomFieldType;
  options: string[];
  placeholder: string | null;
  help_text: string | null;
  /** Character limit for Short text / Long text; null = no limit (the form's own 1000 still applies). */
  max_length: number | null;
  allow_custom: boolean;
  mandatory: boolean;
  enabled: boolean;
  is_system: boolean;
  display_order: number;
  updated_by: string | null;
  updated_at: string;
}

export type SymptomValue = string | string[] | boolean;

export interface SymptomAnswer {
  key: string;
  label: string;
  type: SymptomFieldType;
  value: SymptomValue;
}

export interface SymptomFormSnapshot {
  answers: SymptomAnswer[];
}

/** The one system field: the presenting-symptoms picker, "Why today"'s headline. */
export const PRESENTING_SYMPTOMS_KEY = 'presenting_symptoms';

export const FIELD_TYPE_LABELS: Record<SymptomFieldType, string> = {
  multiselect: 'Searchable multi-select',
  checkboxes: 'Checkbox list',
  dropdown: 'Dropdown',
  radio: 'Single choice (buttons)',
  checkbox: 'Yes / no checkbox',
  text: 'Short text',
  textarea: 'Long text',
  number: 'Number',
  date: 'Date',
};

export const OPTION_TYPES: readonly SymptomFieldType[] = ['dropdown', 'radio', 'multiselect', 'checkboxes'];

/** vitals.symptoms is validated to 2000 characters on amendment. */
const SYMPTOMS_TEXT_MAX = 2000;

export function isAnswered(type: SymptomFieldType, value: SymptomValue | undefined): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (type === 'checkbox') return value === true;
  return String(value).trim() !== '';
}

export function emptyValue(type: SymptomFieldType): SymptomValue {
  if (type === 'multiselect' || type === 'checkboxes') return [];
  if (type === 'checkbox') return false;
  return '';
}

/** The answered fields, in form order, as the snapshot a visit stores. */
export function buildAnswers(fields: readonly SymptomField[], values: Record<string, SymptomValue>): SymptomAnswer[] {
  return fields
    .filter((f) => isAnswered(f.field_type, values[f.field_key]))
    .map((f) => {
      const v = values[f.field_key]!;
      return {
        key: f.field_key,
        label: f.label,
        type: f.field_type,
        value: Array.isArray(v) ? v : typeof v === 'boolean' ? v : String(v).trim(),
      };
    });
}

export function valuesFromAnswers(answers: readonly SymptomAnswer[] | undefined): Record<string, SymptomValue> {
  return Object.fromEntries((answers ?? []).map((a) => [a.key, a.value]));
}

export function formatAnswerValue(a: Pick<SymptomAnswer, 'type' | 'value'>): string {
  if (Array.isArray(a.value)) return a.value.join(', ');
  if (a.type === 'checkbox') return a.value ? 'Yes' : 'No';
  if (a.type === 'date' && typeof a.value === 'string') {
    const d = new Date(`${a.value}T00:00:00`);
    if (!isNaN(d.getTime())) return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return String(a.value);
}

/**
 * "Fever, Headache — Duration: 3–7 days; Severity: Moderate; Additional
 * details: worse at night". The symptoms come first and unlabeled, so the
 * queue card's short preview shows them, not a label.
 */
export function composeSymptomsText(answers: readonly SymptomAnswer[]): string {
  const primary = answers.find((a) => a.key === PRESENTING_SYMPTOMS_KEY);
  const rest = answers
    .filter((a) => a !== primary)
    .map((a) => `${a.label}: ${formatAnswerValue(a)}`);
  const head = primary ? formatAnswerValue(primary) : '';
  const text = head && rest.length ? `${head} — ${rest.join('; ')}` : head || rest.join('; ');
  return text.length > SYMPTOMS_TEXT_MAX ? `${text.slice(0, SYMPTOMS_TEXT_MAX - 1)}…` : text;
}

/** Short text / Long text — the types that can carry a character limit. */
export const TEXT_TYPES: readonly SymptomFieldType[] = ['text', 'textarea'];

/** The longest an answer to this field may be: its own limit, else the form-wide 1000. */
export function maxLengthOf(field: Pick<SymptomField, 'field_type' | 'max_length'>): number | null {
  if (!TEXT_TYPES.includes(field.field_type)) return null;
  return field.max_length ?? 1000;
}

/** Text answers longer than their field allows (e.g. text carried in from intake or voice). */
export function overLimit(fields: readonly SymptomField[], values: Record<string, SymptomValue>): SymptomField[] {
  return fields.filter((f) => {
    const max = maxLengthOf(f);
    const v = values[f.field_key];
    return max !== null && typeof v === 'string' && v.trim().length > max;
  });
}

/** Labels of mandatory fields left blank. */
export function missingMandatory(fields: readonly SymptomField[], values: Record<string, SymptomValue>): SymptomField[] {
  return fields.filter((f) => f.mandatory && !isAnswered(f.field_type, values[f.field_key]));
}

/** The stage_data snapshot, if a stage recorded one. */
export function readSnapshot(stageData: Record<string, unknown> | null | undefined): SymptomFormSnapshot | null {
  const s = stageData?.symptom_form as SymptomFormSnapshot | null | undefined;
  return s && Array.isArray(s.answers) && s.answers.length ? s : null;
}

const FREE_TEXT_TYPES = TEXT_TYPES;

/**
 * The symptoms as the Consultation chat and the AI read them: the selected
 * symptoms lead as the complaint, the other choices follow, and free text is
 * labelled as staff notes rather than merged in, so a long explanatory note
 * is never presented as the patient's symptoms. Mirrors backend
 * utils/symptomForm.js#consultationSymptoms; change both together.
 * Null when the stage has no snapshot (use the plain `symptoms` text then).
 */
export function symptomsForConsultation(stageData: Record<string, unknown> | null | undefined): string | null {
  const snapshot = readSnapshot(stageData);
  if (!snapshot) return null;
  const presenting = snapshot.answers.find((a) => a.key === PRESENTING_SYMPTOMS_KEY);
  const rest = snapshot.answers.filter((a) => a !== presenting);
  const line = (a: SymptomAnswer) => `${a.label}: ${formatAnswerValue(a)}`;
  const details = rest.filter((a) => !FREE_TEXT_TYPES.includes(a.type)).map(line);
  const notes = rest.filter((a) => FREE_TEXT_TYPES.includes(a.type)).map(line);
  const head = [presenting ? formatAnswerValue(presenting) : 'Not selected', details.join('; ')].filter(Boolean).join(' — ');
  return notes.length
    ? `${head}. Staff notes (context only, not the presenting symptoms): ${notes.join('; ')}`
    : head;
}
